import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { ChevronDownIcon } from '@radix-ui/react-icons'
import { useScatterplotContext } from 'features/timeSeries'
import React, { useCallback } from 'react'
import styled from 'styled-components'

export interface SensorSelectOption {
  sensorId: string
  name: string
  color: string
}

const Trigger = styled(DropdownMenu.Trigger)`
  display: flex;
  align-items: center;
  gap: 5px;
  background: color-mix(in oklab, var(--foreground) 6%, transparent);
  border: none;
  border-radius: 3px;
  color: var(--foreground);
  font-family: 'Manrope', sans-serif;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  outline: none;
  padding: 2px 5px;
  min-width: 0;

  &:hover {
    background: color-mix(in oklab, var(--foreground) 10%, transparent);
  }

  &:disabled {
    cursor: default;
    opacity: 0.45;
  }
`

const SensorDot = styled.span<{ $color: string }>`
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`

const TriggerLabel = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  flex: 1;
`

const Content = styled(DropdownMenu.Content)`
  background: var(--popover);
  border: 1px solid var(--border);
  border-radius: 4px;
  padding: 3px;
  z-index: 99999;
  min-width: 180px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
`

const Item = styled(DropdownMenu.Item)<{ $isSelected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'Manrope', sans-serif;
  font-size: 11px;
  font-weight: ${({ $isSelected }) => ($isSelected ? 600 : 400)};
  color: var(--popover-foreground);
  padding: 4px 8px;
  border-radius: 3px;
  cursor: pointer;
  outline: none;

  &[data-highlighted] {
    background: var(--accent);
  }
`

interface SensorSelectProps {
  options: SensorSelectOption[]
  selectedSensorId: string | null
  onSelect: (sensorId: string) => void
  placeholder?: string
  maxWidth?: number
  onMouseDown?: React.MouseEventHandler<HTMLElement>
  disabled?: boolean
}

export const SensorSelect: React.FC<SensorSelectProps> = ({
  options,
  selectedSensorId,
  onSelect,
  placeholder,
  maxWidth = 150,
  onMouseDown,
  disabled = false,
}) => {
  const { t } = useLingui()
  const displayPlaceholder = placeholder ?? t`Select a pen`
  const { setHighlightedSensorId } = useScatterplotContext()

  const selectedOption =
    options.find((option) => option.sensorId === selectedSensorId) ?? null

  const handleMouseEnter = useCallback(
    (sensorId: string) => {
      setHighlightedSensorId(sensorId)
    },
    [setHighlightedSensorId]
  )

  const handleMouseLeave = useCallback(() => {
    setHighlightedSensorId(null)
  }, [setHighlightedSensorId])

  return (
    <DropdownMenu.Root
      onOpenChange={(open) => {
        if (!open) handleMouseLeave()
      }}>
      <Trigger
        disabled={disabled}
        onMouseDown={onMouseDown as React.MouseEventHandler<HTMLButtonElement>}
        onMouseEnter={() => {
          if (selectedOption) handleMouseEnter(selectedOption.sensorId)
        }}
        onMouseLeave={handleMouseLeave}
        style={{ maxWidth }}>
        {selectedOption && <SensorDot $color={selectedOption.color} />}
        <TriggerLabel>
          {selectedOption?.name ?? displayPlaceholder}
        </TriggerLabel>
        <ChevronDownIcon />
      </Trigger>
      <DropdownMenu.Portal>
        <Content
          sideOffset={4}
          align='start'
          onMouseDown={onMouseDown as React.MouseEventHandler<HTMLDivElement>}
          onMouseLeave={handleMouseLeave}>
          {options.map((option) => (
            <Item
              key={option.sensorId}
              $isSelected={option.sensorId === selectedSensorId}
              onMouseEnter={() => handleMouseEnter(option.sensorId)}
              onMouseLeave={handleMouseLeave}
              onSelect={() => {
                onSelect(option.sensorId)
                handleMouseLeave()
              }}>
              <SensorDot $color={option.color} />
              <TriggerLabel>{option.name}</TriggerLabel>
            </Item>
          ))}
        </Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
import { useLingui } from '@lingui/react/macro'
